import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CardContent,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  styled,
  Stack,
} from '@mui/material';

import { ContainerBox, FormCard } from '../../../core/components/styled/StyledComponents';
import { Card } from '../../../core/components';
import {
  CheckIconStyled,
  ContentContainer,
  FeatureItem,
  PlanCardWrapper,
  PlanTitle,
  PopularBadge,
  SavingsText,
} from './style';
import { addOnPlanFaq, faqs } from '../utils/constants';
import { SubscriptionSkeleton } from './SubscriptionSkeleton';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSelectedSubscription,
  setSubscriptionsUiModel,
} from '../../../core/store/slices/subscription.slice';
import { Subscription, SubscriptionUIModel } from '../types/subscription';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  memberStripeCustomerId,
  updateStripeInfo,
} from '../../../core/store/slices/basicInfoSlice';
import { useSubscriptionsQuery } from '../hooks/useSubscriptionsQuery';
import { transformSubscriptionsForUI } from '../utils/transformToUiSubscription';
import { useCreateStripeCustomerMutation } from '../hooks/subscriptionApi';
import { ErrorSource } from '../../../core/constant/model/api/api';
import { UserStatus, EXPECTED_PLAN_COUNT } from '../../../core/constant/constant';
import WaitlistMessage from '../../../core/components/ui/WaitlistMessage';
import { clearApiError } from '../../../core/store/slices/apiError.slice';
import { clearError } from '../../../core/store/slices/auth.slice';
import { RootState } from '../../../core/store';
import { useCompleteEnrollmentMutation } from '../../enrollment/hooks/enrollmentApi';
import { useAppSelector } from '../../../core/hooks/redux';
import { useGetStudentSubscriptionQuery } from '../../billing/api/billing.api';
import { useTrialEligibilityQuery } from '../hooks/useTrialEligibilityQuery';
const VipBadge = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.secondary[400],
  color: theme.palette.primary[700],
  padding: '0.1rem 0.4rem',
  borderRadius: '4px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
  },
  fontSize: '0.8rem',
  width: 'fit-content',
  margin: 'auto',
  fontWeight: 600,
}));
const Subscriptions = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmallMobile = useMediaQuery('(max-width:360px)');
  const [showAllSubscriptions, setShowAllSubscriptions] = useState(false);

  const [processingSubscriptionId, setProcessingSubscriptionId] = useState('');
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);

  const stripeCustomerId = useSelector(memberStripeCustomerId);
  const completeEnrollmentMutation = useCompleteEnrollmentMutation();
  const navigate = useNavigate();
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const authFlow = useSelector((state: RootState) => state.auth.authFlow);
  const [searchParams, setSearchParams] = useSearchParams();
  const isAddOn = Boolean(searchParams.get('addOn'));
  const isNewEnrollmentParam = Boolean(searchParams.get('isNewEnrollment'));
  // For COMMUNITY_MEMBER, treat as new enrollment even if user logged in (not signed up)
  const isCommunityMemberFlow = basicInfo?.currentStatus === UserStatus.COMMUNITY_MEMBER;
  const isNewEnrollment = isNewEnrollmentParam && (authFlow !== 'login' || isCommunityMemberFlow);
  const hasPartner = basicInfo?.partnerId !== 'NONE';
  const currentSubscription = useAppSelector((state) => state.memberSubscription);
  const {
    data: rawSubscriptions,
    isLoading: isLoadingSubscriptions,
    error: subscriptionsError,
  } = useSubscriptionsQuery({ params: `isAddOn=${isAddOn}` });

  const createStripeCustomerMutation = useCreateStripeCustomerMutation();
  const { data } = useGetStudentSubscriptionQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const isSubscriptionAvailable = data?.data?.data?.status;
  
  // Check trial eligibility (only for new enrollments, not add-ons)
  const { data: trialEligibilityData, isLoading: isLoadingTrialEligibility } = useTrialEligibilityQuery(
    basicInfo._id,
    !isAddOn && !!basicInfo._id
  );
  const isTrialUsed = trialEligibilityData?.data?.trialUsed ?? true;

  useEffect(() => {
    if (!isLoadingTrialEligibility && trialEligibilityData?.data != null) {
      console.log('[Subscriptions] Trial eligibility:', {
        trialUsed: isTrialUsed,
        resolvedTrialUsed: isTrialUsed,
      });
    }
  }, [isLoadingTrialEligibility, trialEligibilityData, isTrialUsed]);

  const subscriptions = React.useMemo(() => {
    if (!rawSubscriptions?.data) return [];

    const subscriptionData = (rawSubscriptions.data as Subscription[]) || [];

    const sortedData = [...subscriptionData].sort((a, b) => {
      return Number(a.price.unit_amount) - Number(b.price.unit_amount);
    });

    return transformSubscriptionsForUI(sortedData);
  }, [rawSubscriptions]);

  useEffect(() => {
    if (subscriptions.length > 0) {
      dispatch(setSubscriptionsUiModel(subscriptions));
    }
  }, [subscriptions, dispatch]);

  useEffect(() => {

    if (authFlow === 'login' && isNewEnrollmentParam && !isCommunityMemberFlow) {
      const params = new URLSearchParams(searchParams);
      params.delete('isNewEnrollment');
      setSearchParams(params, { replace: true });
    }
  }, [authFlow, isNewEnrollmentParam, searchParams, setSearchParams, isCommunityMemberFlow]);

  const visibleSubscriptions =
    isSmallMobile && !showAllSubscriptions ? subscriptions?.slice(0, 3) : subscriptions;

  /** Normalize plan features: coach→partner, replace coaches bullet, add missing first two bullets for 50/100 plans */
  const getDisplayFeatures = useCallback((subscription: SubscriptionUIModel): string[] => {
    const mapFeature = (text: string) =>
      text
        .replace(/\bto your coach\b/gi, 'to your partner')
        .replace(/\bAccess to trained coaches\b/gi, 'Get matched with your personal emotional support partner');
    let features = (subscription.features || []).map(mapFeature);
    const moments = subscription.moments;
    const hasRecurring = features.some((f) => /monthly recurring membership/i.test(f));
    if (moments === 50 && !hasRecurring) {
      features = ['Monthly recurring membership', '50 Moments = 50 texts to your partner', ...features];
    } else if (moments === 100 && !hasRecurring) {
      features = ['Monthly recurring membership', '100 Moments = 100 texts to your partner', ...features];
    }
    return features;
  }, []);

  const createStripeCustomer = useCallback(async () => {
    if (isCreatingCustomer) {
      return;
    }

    dispatch(clearApiError(ErrorSource.CREATE_STRIPE_CUSTOMER));
    dispatch(clearError());

    setIsCreatingCustomer(true);

    try {
      const response = await createStripeCustomerMutation.mutateAsync();

      if (response.data?.customerId) {
        dispatch(updateStripeInfo(response.data.customerId));
      }
      return response;
    } catch (error) {
      // console.error('Error creating Stripe customer:', error);
      // throw error;
    } finally {
      setIsCreatingCustomer(false);
    }
  }, [dispatch, createStripeCustomerMutation, isCreatingCustomer]);

  const handleSubscriptionSelect = useCallback(
    async (subscription: SubscriptionUIModel) => {
      if (processingSubscriptionId || isCreatingCustomer) return;

      dispatch(setSelectedSubscription(subscription));

      setProcessingSubscriptionId(subscription?.id);

      try {
        if (!hasPartner && Number(subscription.price) === 0) {
          const memberId = basicInfo._id;
          try {
            await completeEnrollmentMutation.mutateAsync({ memberId: memberId! });
            navigate('/application-received', { replace: true });
            return;
          } catch (error: any) {
            if (
              error?.statusCode === 400 &&
              error?.message === 'User is still in onboarding process.'
            ) {
              navigate('/enrollment', { replace: true });
              return;
            }
            throw error;
          }
        }

        if (!stripeCustomerId) {
          await createStripeCustomer();
        }
        const params = new URLSearchParams();
        if (isAddOn) {
          params.append('addOn', 'true');
        }
        if (isNewEnrollment) {
          params.append('isNewEnrollment', 'true');
        }
        navigate('/checkout?' + params);
      } catch (error: any) {
        console.error('Error creating Stripe customer:', error);
      } finally {
        setProcessingSubscriptionId('');
      }
    },
    [
      processingSubscriptionId,
      isCreatingCustomer,
      dispatch,
      stripeCustomerId,
      createStripeCustomer,
      navigate,
      hasPartner,
      completeEnrollmentMutation,
      isAddOn,
      isNewEnrollment,
      basicInfo._id,
    ]
  );

  const renderSubscriptionButton = useCallback(
    (subscription: SubscriptionUIModel) => {
      const isProcessing =
        processingSubscriptionId === subscription.id ||
        (isCreatingCustomer && processingSubscriptionId === subscription.id);
      const disabledCurrentCard =
        currentSubscription?.priceId === subscription.priceId &&
        isSubscriptionAvailable === 'ACTIVE';

      return (
        <Button
          variant='contained'
          sx={{
            backgroundColor: subscription.isPopular
              ? theme.palette.primary[400]
              : theme.palette.secondary[500],
            color: subscription.isPopular ? '#FFFFFF' : theme.palette.primary.main,
            '&:hover': {
              backgroundColor: subscription.isPopular
                ? theme.palette.primary[500]
                : theme.palette.secondary[600],
            },
            fontWeight: 600,
            borderRadius: isSmallMobile ? '4px' : '8px',
            padding: isSmallMobile ? '8px 10px' : subscription.isPopular ? '10px 0' : '10px 0',
            textTransform: 'uppercase',
            fontSize: isSmallMobile ? '0.7rem' : '0.875rem',
            letterSpacing: '0.5px',
            boxShadow: 'none',
            border: 'none',
            minWidth: isSmallMobile ? '90px' : 'auto',
            minHeight: isSmallMobile ? '36px' : 'auto',
            width: !isSmallMobile ? '100%' : 'auto',
            whiteSpace: 'nowrap',
          }}
          disableElevation
          onClick={() => handleSubscriptionSelect(subscription)}
          disabled={!!processingSubscriptionId || isCreatingCustomer || disabledCurrentCard}
        >
          {isProcessing ? (
            <CircularProgress size={20} color='inherit' />
          ) : isAddOn ? (
            'ADD MOMENTS'
          ) : isTrialUsed === false ? (
            'TRY FOR FREE'
          ) : (
            'GET STARTED'
          )}
        </Button>
      );
    },
    [processingSubscriptionId, isCreatingCustomer, isSmallMobile, handleSubscriptionSelect, isTrialUsed, isAddOn]
  );

  const renderSubscriptionHeadingSection = useCallback(() => {
    return (
      <>
        <Box
          sx={{
            textAlign: 'center',
            pt: isSmallMobile ? 3 : 0,
          }}
          mb={4}
        >
          <Typography
            variant='h3'
            component='h1'
            sx={{
              color: theme.palette.primary[400],
              fontWeight: 600,
              mb: { xs: 0.5, sm: 1, md: 2 },
              fontSize: isExtraSmallMobile
                ? '1.35rem'
                : isSmallMobile
                  ? '1.5rem'
                  : isMobile
                    ? '1.75rem'
                    : '2.5rem',
              lineHeight: 1.2,
            }}
          >
            Choose Your MindRight Support Plan
          </Typography>
          <Typography
            variant='h5'
            component='h1'
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              mb: { xs: 0.5, sm: 1, md: 1.5 },
              fontSize: isExtraSmallMobile
                ? '1.35rem'
                : isSmallMobile
                  ? '1.5rem'
                  : isMobile
                    ? '1.75rem'
                    : '2.5rem',
              lineHeight: 1.2,
            }}
          >
            You Deserve A Moment
          </Typography>
          <Typography
            variant='subtitle1'
            sx={{
              color: '#666666',
              fontSize: isSmallMobile ? '0.85rem' : isMobile ? '0.95rem' : '1.08rem',
              px: isSmallMobile ? 1 : 0,
            }}
          >
            Mental health coaching created by our communities, for our communities
          </Typography>
        </Box>
        <Box
          borderTop={2}
          borderColor={theme.palette.divider}
          pt={4}
          mb={5}
          maxWidth={'580px'}
          mx={'auto'}
        >
          <Typography
            variant='h6'
            fontWeight={600}
            textAlign={'center'}
            sx={{
              color: theme.palette.primary[400],
              mx: 'auto',
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            MindRight Moments
          </Typography>
          <Typography
            variant='h6'
            textAlign={'center'}
            sx={{
              color: theme.palette.primary.main,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 2,
              fontSize: isSmallMobile ? '0.85rem' : '0.95rem',
            }}
          >
            connect you with mental health support that respects where you come from and sees you
            fully
          </Typography>
          <Typography
            variant='h6'
            textAlign={'center'}
            sx={{
              color: theme.palette.primary.main,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 2,
              fontSize: isSmallMobile ? '0.85rem' : '0.95rem',
            }}
          >
            Send messages to partners who understand your journey — their responses are always FREE
          </Typography>
        </Box>
      </>
    );
  }, []);

  const renderAddOnHeadingSection = useCallback(() => {
    return (
      <>
        <Box
          sx={{
            textAlign: 'center',
            pt: isSmallMobile ? 3 : 0,
          }}
          mb={4}
        >
          <Typography
            variant='h3'
            component='h1'
            sx={{
              color: theme.palette.primary[400],
              fontWeight: 600,
              mb: { xs: 0.5, sm: 1, md: 2 },
              fontSize: isExtraSmallMobile
                ? '1.35rem'
                : isSmallMobile
                  ? '1.5rem'
                  : isMobile
                    ? '1.75rem'
                    : '2.5rem',
              lineHeight: 1.2,
            }}
          >
            Need a Little More Support? Try a One-Time Add-On.
          </Typography>
          <Typography
            variant='h5'
            component='h1'
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              mb: { xs: 0.5, sm: 1, md: 1.5 },
              fontSize: isExtraSmallMobile
                ? '1.35rem'
                : isSmallMobile
                  ? '1.5rem'
                  : isMobile
                    ? '1.75rem'
                    : '2.5rem',
              lineHeight: 1.2,
            }}
          >
            No Commitment. No pressure. Just more MindRight Moments, your way.
          </Typography>
        </Box>
      </>
    );
  }, []);
  const _faq = isAddOn ? addOnPlanFaq : faqs;
  return (
    <ContainerBox sx={{ padding: '0px' }}>
      <FormCard sx={{ boxShadow: 'none' }}>
        <ContentContainer sx={{ backgroundColor: '#F6F7FB' }}>
          {isAddOn ? renderAddOnHeadingSection() : renderSubscriptionHeadingSection()}

          {isLoadingSubscriptions ? (
            <SubscriptionSkeleton isSmallMobile={isSmallMobile} />
          ) : subscriptionsError ? (
            <Box sx={{ textAlign: 'center', my: 4, color: '#666666' }}>
              <Typography variant='body1'>
                Unable to load subscription plans. Please try again later.
              </Typography>
              <Button
                variant='outlined'
                sx={{ mt: 2, color: '#5C5AF0', borderColor: '#5C5AF0' }}
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </Box>
          ) : isSmallMobile ? (
            <Box sx={{ mb: 3, mt: 2 }}>
              {visibleSubscriptions?.map((subscription, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    position: 'relative',
                    zIndex: subscription.isPopular ? 2 : 1,
                  }}
                >
                  <PlanCardWrapper isPopular={subscription.isPopular}>
                    {subscription.isPopular && <PopularBadge>POPULAR</PopularBadge>}
                    <Card
                      elevation={0}
                      sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        padding: 0,
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
                        ...(subscription.isPopular && {
                          py: 2,
                          paddingTop: '1.5rem',
                        }),
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <CardContent
                          sx={{
                            textAlign: 'center',
                            pb: 1,
                            px: 2,
                          }}
                        >
                          <PlanTitle variant='h3'>
                            {subscription.title}{' '}
                            {!isAddOn && (
                              <VipBadge
                                variant='caption'
                                sx={{ display: 'inline-block', marginLeft: 1 }}
                              >
                                VIP
                              </VipBadge>
                            )}
                          </PlanTitle>
                        </CardContent>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            px: 2,
                            pb: 2,
                            pt: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: '30%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant='h3'
                              sx={{
                                fontSize: '2rem',
                                fontWeight: 600,
                                color: theme.palette.primary[400],
                                lineHeight: 1,
                                mb: 2,
                                textAlign: 'left',
                              }}
                            >
                              {`$${subscription.price}`}
                            </Typography>

                            <Typography
                              variant='body2'
                              sx={{
                                color: '#666666',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                mb: 1,
                                textAlign: 'left',
                              }}
                            >
                              {subscription.moments} MindRight Moments
                            </Typography>

                            {subscription.savings && (
                              <SavingsText
                                variant='h6'
                                sx={{
                                  fontSize: '0.7rem',
                                  m: 0,
                                  textAlign: 'left',
                                  maxWidth: '100%',
                                }}
                              >
                                {subscription.savings}
                              </SavingsText>
                            )}
                          </Box>

                          <Box
                            sx={{
                              width: '40%',
                              pl: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                            }}
                          >
                            {getDisplayFeatures(subscription).map((feature, i) => (
                              <FeatureItem key={i}>
                                <CheckIconStyled fontSize='small' />
                                <Typography
                                  variant='body2'
                                  sx={{
                                    color: '#555555',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {feature}
                                </Typography>
                              </FeatureItem>
                            ))}
                          </Box>

                          <Box
                            sx={{
                              width: '30%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              pl: 1,
                            }}
                          >
                            {renderSubscriptionButton(subscription)}
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </PlanCardWrapper>
                </Box>
              ))}

              {subscriptions?.length > 3 && (
                <Box sx={{ textAlign: 'center', mt: 2, mb: 4 }}>
                  <Button
                    variant='text'
                    sx={{
                      color: '#5C5AF0',
                      fontSize: '0.8rem',
                      textTransform: 'none',
                    }}
                    onClick={() => setShowAllSubscriptions(!showAllSubscriptions)}
                  >
                    {showAllSubscriptions ? 'Show fewer plans' : 'View more plans'}
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  sm: 'repeat(2, 1fr)',
                  md: subscriptions?.length === 3 ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
                },
                gap: { sm: 2, md: 3 },
                mb: { sm: 6, md: 10 },
                position: 'relative',
                alignItems: 'stretch',
                maxWidth: subscriptions?.length === 3 ? '900px' : '100%',
                mx: 'auto',
              }}
            >
              {subscriptions?.map((subscription, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    position: 'relative',
                    zIndex: subscription.isPopular ? 2 : 1,
                  }}
                >
                  <PlanCardWrapper isPopular={subscription.isPopular}>
                    {subscription.isPopular && <PopularBadge>POPULAR</PopularBadge>}
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        padding: 0,
                        ...(subscription.isPopular && {
                          py: 2,
                          paddingTop: '1.5rem',
                        }),
                      }}
                    >
                      <CardContent
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        <PlanTitle variant='h6'>
                          {subscription.title}
                          {!isAddOn && (
                            <VipBadge
                              variant='caption'
                              sx={{ display: 'inline-block', marginLeft: 1 }}
                            >
                              VIP
                            </VipBadge>
                          )}
                        </PlanTitle>

                        <Typography
                          variant='h3'
                          sx={{
                            fontSize: isMobile ? '2.5rem' : '3rem',
                            fontWeight: 600,
                            color: theme.palette.primary[400],
                            marginBottom: 0.5,
                            marginTop: 1,
                            lineHeight: 1.1,
                            textShadow: `0 0 1px ${theme.palette.primary[400]}`,
                            letterSpacing: '-0.5px',
                          }}
                        >
                          {`$${subscription.price}`}
                        </Typography>

                        <Typography
                          variant='subtitle1'
                          sx={{
                            color: '#666666',
                            mb: 1,
                            mt: 2,
                            fontWeight: 500,
                            fontSize: isMobile ? '0.9rem' : '0.95rem',
                          }}
                        >
                          {subscription.moments} MindRight Moments
                        </Typography>

                        {subscription.savings && (
                          <SavingsText variant='h6'>{subscription.savings}</SavingsText>
                        )}
                      </CardContent>

                      <Box sx={{ px: 3, mb: 3, flexGrow: 1, mt: subscription.isPopular ? 3 : 0 }}>
                        {getDisplayFeatures(subscription).map((feature, i) => (
                          <FeatureItem key={i}>
                            <CheckIconStyled fontSize='small' />
                            <Typography
                              variant='body2'
                              sx={{
                                color: '#555555',
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                              }}
                            >
                              {feature}
                            </Typography>
                          </FeatureItem>
                        ))}
                      </Box>

                      <Box sx={{ px: 3, pb: subscription.isPopular ? 3 : 1, pt: 1, mt: 'auto' }}>
                        {renderSubscriptionButton(subscription)}
                      </Box>
                    </Card>
                  </PlanCardWrapper>
                </Box>
              ))}
            </Box>
          )}

          {/* Waitlist message — shown when fewer plans are active than expected */}
          {!isLoadingSubscriptions && (
            <WaitlistMessage visible={subscriptions.length < EXPECTED_PLAN_COUNT} />
          )}

          {/* Trial Messaging - Only show when trial not yet used, not add-ons */}
          {!isAddOn && !isLoadingTrialEligibility && isTrialUsed === false && (
            <Box
              sx={{
                mb: { xs: 4, sm: 5, md: 6 },
                mt: { xs: 3, sm: 4, md: 5 },
                textAlign: 'center',
                px: { xs: 2, sm: 3, md: 4 },
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  p: { xs: 3, sm: 4, md: 4 },
                  backgroundColor: '#F0F4FF',
                  border: '1px solid #E0E7FF',
                }}
              >
                <Typography
                  variant='h5'
                  sx={{
                    color: theme.palette.primary[400],
                    fontWeight: 600,
                    mb: 2,
                    fontSize: isSmallMobile ? '1.1rem' : isMobile ? '1.25rem' : '1.5rem',
                  }}
                >
                  Try MindRight free — no commitment required
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    color: theme.palette.primary[700],
                    lineHeight: 1.6,
                    mb: 1.5,
                    fontSize: isSmallMobile ? '0.875rem' : isMobile ? '0.95rem' : '1rem',
                  }}
                >
                  Begin with one free session to use anytime within 7 days. No pressure—cancel
                  anytime before your trial ends. If you continue, your membership will
                  automatically start based on your selected plan.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: theme.palette.primary[600],
                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                    fontStyle: 'italic',
                  }}
                >
                  Free trial available once per member.
                </Typography>
              </Paper>
            </Box>
          )}

          {!isSmallMobile && <Box sx={{ height: { xs: 20, sm: 30, md: 50 } }} />}

          <Box
            sx={{
              mb: { xs: 4, sm: 6, md: 8 },
              textAlign: 'center',
              px: { xs: 1, sm: 2, md: 4 },
              pt: { xs: 2, sm: 3, md: 6 },
              pb: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Typography
              variant='body1'
              align='center'
              sx={{
                fontStyle: 'italic',
                mb: 1,
                color: theme.palette.primary[700],
                lineHeight: 1.5,
                fontSize: isSmallMobile ? '0.85rem' : isMobile ? '0.95rem' : '1.05rem',
              }}
            >
              "Having a partner to text whenever I'm feeling anxious has completely changed how I
              manage my mental health. Each MindRight Moment feels like an investment in myself."
            </Typography>
            <Typography
              variant='body2'
              align='center'
              sx={{
                color: theme.palette.primary[400],
                fontWeight: 600,
                fontSize: isSmallMobile ? '0.75rem' : 'inherit',
              }}
            >
              — Jamie S.
            </Typography>
          </Box>

          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                p: { xs: 3, sm: 3, md: 4 },
                pt: { xs: 4, sm: 4, md: 4 },
                pb: { xs: 4, sm: 4, md: 4 },
                backgroundColor: '#FFFFFF',
                border: '1px solid #EEEDFF',
              }}
            >
              <Typography
                variant='h4'
                align='center'
                sx={{
                  mb: { xs: 4, sm: 4, md: 5 },
                  color: theme.palette.primary[400],
                  fontWeight: 600,
                  fontSize: isExtraSmallMobile
                    ? '1.25rem'
                    : isSmallMobile
                      ? '1.35rem'
                      : isMobile
                        ? '1.5rem'
                        : '1.75rem',
                }}
              >
                {isAddOn ? 'Commonly Asked Questions' : 'Common Questions'}
              </Typography>
              <Stack spacing={5}>
                {_faq.map((faq, index) => (
                  <Box key={index}>
                    <Typography
                      variant='subtitle1'
                      sx={{
                        color: theme.palette.primary[500],
                        fontWeight: 600,
                        mb: 1,
                        fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem',
                      }}
                    >
                      {faq.question}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: theme.palette.primary[800],
                        lineHeight: 1.6,
                        fontSize: isSmallMobile ? '0.75rem' : isMobile ? '0.8rem' : '0.875rem',
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Box>
          {isSmallMobile && <Box sx={{ height: 20 }} />}
        </ContentContainer>
      </FormCard>
    </ContainerBox>
  );
};

export default React.memo(Subscriptions);
